import React from 'react';
import {FormattedMessage} from 'react-intl';
import {graphql, useMutation} from 'react-relay/hooks';
import {useForm} from 'react-hook-form';
import useQuery from '../../../useQuery';
import useMountedState from '../../../useMountedState';
import {RouterLinkButton} from '../../../components/button/button';
import {CheckoutDispatchContext} from '../../../components/CheckoutContext';
import {DialogDispatchContext} from '../../../components/DialogContext';
import {useErrorMessagesDispatch} from '../error-messages-context';
import {FetchKeySetStateContext} from '../../../components/FetchKey';
import ArrowRightIcon from '../../../assets/icons0/arrow-right.svg';
import useImpulseService from './use-impulse-service';

const RAZZLE_GRECAPTCHA = process.env.RAZZLE_GRECAPTCHA;
const RAZZLE_TECHNICAL_MERCHANT = process.env.RAZZLE_TECHNICAL_MERCHANT;

const useCreateImpulse = ({dispatch, currentLandPicks, coupon, serviceId, setNewImpulseId, resetInvalidEndpoints, invalidEndpoints, oplId = null}) => {
    const {locale = 'uk'} = useQuery();
    const {register, handleSubmit, formState, setValue} = useForm();
    const [grecaptcha, setGrecaptcha] = useMountedState(false);
    const checkoutDispatch = React.useContext(CheckoutDispatchContext);
    const dialogDispatch = React.useContext(DialogDispatchContext);
    const errorDispatch = useErrorMessagesDispatch();
    const [isPending, startTransition] = React.useTransition();
    const fetchKeySetState = React.useContext(FetchKeySetStateContext);
    const {serviceById, serviceUrl} = useImpulseService({serviceId});

    const [commit, isMutationInFlight] = useMutation(
        graphql`
            mutation useCreateImpulseCreateImpulseMutation($impulseRequest: ImpulseRequestInput!) {
                createImpulse(impulseRequest: $impulseRequest) {
                    id
                    orderDates
                    amount
                    impulseService {
                        service {
                            id
                            name
                        }
                        independenceType
                        merchantAccount
                        partnerCode
                        merchantDomainName
                        holdTimeout
                        manual
                        mrchntd
                        uaPayMerchantId
                    }
                    impulseEndpoints {
                        name
                        price
                        quantity
                    }
                    impulseClient {
                        email
                    }
                    feeAmount
                    amountAmount
                    orderReferences
                    merchantSignatures
                    claim
                }
            }
    `);
    
    const createImpulse = handleSubmit(formValue => {
        errorDispatch({type: 'reset', payload: []});
        if (!window.grecaptcha) {
            errorDispatch({type: 'add', payload: {errorCode: 'windowRecaptcha'}});
            return;
        }
        if (serviceById.mrchntd === RAZZLE_TECHNICAL_MERCHANT && (!serviceById.pools || (serviceById.pools && !serviceById.pools.length))) {
            errorDispatch({type: 'add', payload: {errorCode: 'technicalMerchantPools'}});
            return;
        }
        if (serviceById.mrchntd !== RAZZLE_TECHNICAL_MERCHANT && serviceById.pools && serviceById.pools.length) {
            errorDispatch({type: 'add', payload: {errorCode: 'serviceHasPools'}});
            return;
        }
        if (invalidEndpoints.length) {
            errorDispatch({
                type: 'add',
                payload: {
                    type: 'lightError',
                    title: <FormattedMessage defaultMessage='There are unavailable tickets in the order'/>,
                    description: <FormattedMessage defaultMessage='To place an order, return to the event page and select available tickets'/>,
                    showDefaultAction: false,
                    action: <RouterLinkButton {...{to: serviceUrl, plain: true, color: 'error'}}><span className=''><FormattedMessage defaultMessage='To event'/></span><ArrowRightIcon/></RouterLinkButton>
                }
            });
            return;
        }
        setGrecaptcha(true);
        window.grecaptcha.ready(() => {
            window.grecaptcha.execute(RAZZLE_GRECAPTCHA, {action: 'submit'}).then((token) => {
                const request = {
                    locale,
                    email: formValue.email,
                    reCaptcha: token,
                    ...(coupon && {impulseCoupon: {code: coupon.code}}),
                    service: serviceId,
                    impulseEndpoints: currentLandPicks.map((item) => ({
                        endpoint: item.endpointId,
                        quantity: item.quantity,
                        ...(item.untie && {untie: {
                            currency: item.untie.currency,
                            price: item.untie.price
                        }})
                    })),
                    impulseAddls: formValue.addls && Object.entries(formValue.addls).map(([key, value]) => ({
                        addl: key,
                        value: value === true ? '+' : value === false ? '-' : value
                    })),
                };
                if (oplId) {
                    request.impulseBasket = {
                        basket: oplId
                    }
                }
                commit({
                    variables: {
                        impulseRequest: request
                    },
                    onCompleted: (data) => {
                        startTransition(() => {
                            checkoutDispatch({type: 'add', payload: {id: data.createImpulse.id, claim: data.createImpulse.claim}});
                            data.createImpulse.impulseService.manual &&
                                dialogDispatch({type: 'add', payload: {impulseId: data.createImpulse.id, claim: data.createImpulse.claim}});
                            dispatch({type: 'bulkRemove', payload: currentLandPicks.map(item => item.key)});
                            setNewImpulseId(data.createImpulse.id);
                        })
                    },
                    onError: (error) => {
                        if (/reCaptcha/i.test(error)) {
                            errorDispatch({type: 'add', payload: {errorCode: 'serverRecaptcha', additionalMessage: `reCaptcha error: ${error}`}});
                        } else if (/Service is invalid/i.test(error)) {
                            errorDispatch({type: 'add', payload: {errorCode: 'serviceInvalid'}});
                        } else if (/Endpoint invalid/i.test(error)) {
                            errorDispatch({
                                type: 'add',
                                payload: {
                                    type: 'lightError',
                                    title: <FormattedMessage defaultMessage='There are unavailable tickets in the order'/>,
                                    description: <FormattedMessage defaultMessage='To place an order, return to the event page and select available tickets'/>,
                                    showDefaultAction: false,
                                    action: <RouterLinkButton {...{to: serviceUrl, plain: true, color: 'error'}}><span className=''><FormattedMessage defaultMessage='To event'/></span><ArrowRightIcon/></RouterLinkButton>
                                }
                            });
                            fetchKeySetState((e) => e + 1);
                            resetInvalidEndpoints();
                        } else {
                            errorDispatch({type: 'add', payload: {errorCode: null}});
                        }
                    },
                    updater: store => {
                        const latestImpulses = store.get('client:root:viewer:__LatestImpulses_impulsesByViewer_connection(sent:true,status:[3,4])');
                        latestImpulses && latestImpulses.invalidateRecord();
                    }
                });
                setGrecaptcha(false);
            });
        });
    });

    return {
        createImpulse,
        isMutationInFlight,
        grecaptcha,
        register,
        formState,
        setValue
    }
};

export default useCreateImpulse;
